import React, { useEffect, useMemo, useState } from 'react';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { School } from 'models/school';
import { getAllSchoolsIncludedInUnion } from 'services/schoolUnion/schools';
import { getPublicSiteLink } from 'helpers/link';
import Loader from 'components/loader';
import './MemberSchools.scss';

interface Props { school: School; }
const PAGE_SIZE = 50;
const shuffled = <T,>(items: T[]) => {
   const result = [...items];
   for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
   }
   return result;
};

export default function MemberSchools({ school }: Props) {
   const [schools, setSchools] = useState<School[]>([]);
   const [search, setSearch] = useState('');
   const [page, setPage] = useState(1);
   const [isLoading, setIsLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);

   useEffect(() => {
      let active = true;
      setIsLoading(true);
      setLoadError(false);
      getAllSchoolsIncludedInUnion(school.id)
         .then(items => { if (active) setSchools(shuffled(items.filter(item => item.kind === 'School'))); })
         .catch(() => { if (active) { setSchools([]); setLoadError(true); } })
         .finally(() => { if (active) setIsLoading(false); });
      return () => { active = false; };
   }, [school.id]);

   const filtered = useMemo(() => {
      const value = search.trim().toLowerCase();
      if (!value) return schools;
      return schools.filter(item => [item.name, item.address, item.county, item.postcode?.postcode]
         .some(field => String(field || '').toLowerCase().includes(value)));
   }, [schools, search]);
   const visible = filtered.slice(0, page * PAGE_SIZE);

   if (isLoading) return <div className='union-schools-loading'><Loader /></div>;
   return <main className='union-schools-page'>
      <header className='union-schools-header'>
         <div><span>Our community</span><h1>Member schools</h1><p>{schools.length} schools are part of {school.name}.</p></div>
         <label><SearchRoundedIcon /><input value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} placeholder='Search school, area or postcode' aria-label='Search member schools' /></label>
      </header>
      {!loadError && visible.length ? <section className='union-schools-wall'>
         {visible.map((member, index) => {
            const location = member.county || member.postcode?.region || member.address || '';
            const content = <><span className='union-school-card__number'>{String(index + 1).padStart(2, '0')}</span><div className='union-school-card__logo'>{member.pic ? <img src={member.pic} alt='' /> : <span>{member.name.split(/\s+/).slice(0, 2).map(value => value[0]).join('')}</span>}</div><div className='union-school-card__copy'><span>{member.areaName ? `Member school · ${member.areaName}` : 'Member school'}</span><h2>{member.name}</h2>{location && <p>{location}</p>}{member.postcode?.postcode && <small><LocationOnOutlinedIcon />{member.postcode.postcode}</small>}</div>{member.domain && <ArrowOutwardRoundedIcon className='union-school-card__arrow' />}</>;
            return member.domain
               ? <a className={`union-school-card is-tone-${index % 5}`} href={getPublicSiteLink(member.domain)} target='_blank' rel='noreferrer' key={member.id}>{content}</a>
               : <article className={`union-school-card is-tone-${index % 5}`} key={member.id}>{content}</article>;
         })}
      </section> : <div className='union-schools-empty' role={loadError ? 'alert' : undefined}><GroupsRoundedIcon /><strong>{loadError ? 'Member schools could not be loaded' : schools.length ? 'No schools found' : 'No member schools published yet'}</strong><span>{loadError ? 'Please refresh the page and try again.' : schools.length ? 'Try a different school name, area or postcode.' : `Schools joining ${school.name} will appear here.`}</span></div>}
      {!loadError && visible.length < filtered.length && <button className='union-schools-more' type='button' onClick={() => setPage(value => value + 1)}><span>Keep exploring</span><strong>Meet the next {Math.min(PAGE_SIZE, filtered.length - visible.length)} schools</strong><small>{visible.length} of {filtered.length} discovered</small></button>}
   </main>;
}
